import React, { useState, useEffect } from 'react';
import {
  ShoppingCart,
  Plus,
  Search,
  Truck,
  Building2,
  Calendar,
  CheckCircle2,
  AlertTriangle,
  FileText,
  ShieldCheck,
  Receipt,
  Clock,
  ArrowRight,
  Filter,
  DollarSign,
  FileCheck2,
  MessageSquare,
} from 'lucide-react';
import {
  PurchaseOrderItem,
  VendorItem,
  SupplierBillItem,
  PurchaseRequisitionItem,
  SupplierExemptionDocItem,
} from '../types/index.ts';
import { WhatsAppShareModal, WhatsAppShareData } from '../components/common/WhatsAppShareModal.tsx';
import { calculateSupplierBillTaxes } from '../services/taxEngine.ts';

interface PurchasePageProps {
  onNavigate: (route: string, param?: string) => void;
}

export const PurchasePage: React.FC<PurchasePageProps> = ({ onNavigate }) => {
  const [activeTab, setActiveTab] = useState<'POS' | 'BILLS' | 'REQUISITIONS' | 'SUPPLIERS'>('POS');

  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrderItem[]>([]);
  const [supplierBills, setSupplierBills] = useState<SupplierBillItem[]>([]);
  const [requisitions, setRequisitions] = useState<PurchaseRequisitionItem[]>([]);
  const [exemptions, setExemptions] = useState<SupplierExemptionDocItem[]>([]);
  const [vendors, setVendors] = useState<VendorItem[]>([]);
  const [projects, setProjects] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // Modals
  const [showAddPoModal, setShowAddPoModal] = useState(false);
  const [showAddBillModal, setShowAddBillModal] = useState(false);
  const [showAddPrModal, setShowAddPrModal] = useState(false);
  const [showAddExemptionModal, setShowAddExemptionModal] = useState(false);
  const [billError, setBillError] = useState<string | null>(null);

  // WhatsApp Sharing State
  const [whatsAppModalOpen, setWhatsAppModalOpen] = useState(false);
  const [shareData, setShareData] = useState<WhatsAppShareData | null>(null);

  // Form states
  const [poForm, setPoForm] = useState({
    projectId: '',
    vendorId: '',
    deliveryDate: '',
    subtotal: '',
    taxAmount: '0',
    terms: '30 Days Net from delivery verification',
  });

  const [billForm, setBillForm] = useState({
    supplierInvoiceNumber: '',
    vendorId: '',
    poId: '',
    projectId: '',
    costCenterType: 'PROJECT_SITE' as 'PROJECT_SITE' | 'GENERAL_COMPANY',
    billDate: new Date().toISOString().split('T')[0],
    dueDate: new Date(Date.now() + 30 * 86400000).toISOString().split('T')[0],
    grossBeforeTax: '',
    otherCharges: '0',
    isGstInvoice: true,
    purchaseType: 'STANDARD_PO' as any,
    remarks: '',
  });

  const [prForm, setPrForm] = useState({
    projectId: '',
    requiredDate: new Date(Date.now() + 7 * 86400000).toISOString().split('T')[0],
    department: 'Site Operations',
    urgencyLevel: 'MEDIUM' as 'LOW' | 'MEDIUM' | 'HIGH' | 'EMERGENCY',
    isDirectSiteDelivery: true,
    reason: 'Initial construction and finishing material for branch renovation',
    itemDescription: 'Porcelain Floor Tiles 600x600',
    itemQuantity: '50',
    itemUnit: 'Carton',
    itemEstimatedRate: '2800',
  });

  const [exemptionForm, setExemptionForm] = useState({
    vendorId: '',
    documentType: 'INCOME_TAX_EXEMPTION_153',
    referenceNumber: '',
    issuingAuthority: 'Federal Board of Revenue (FBR)',
    issueDate: new Date().toISOString().split('T')[0],
    validFrom: new Date().toISOString().split('T')[0],
    validTo: new Date(Date.now() + 365 * 86400000).toISOString().split('T')[0],
    remarks: 'Valid Section 153 exemption verified on IRIS active database',
  });

  const fetchPurchaseData = async () => {
    try {
      setLoading(true);
      const [poRes, vRes, pRes, bRes, rRes, eRes] = await Promise.all([
        fetch('/api/purchase-orders'),
        fetch('/api/vendors'),
        fetch('/api/projects'),
        fetch('/api/supplier-bills'),
        fetch('/api/purchase-requisitions'),
        fetch('/api/supplier-exemptions'),
      ]);

      if (poRes.ok) {
        const poData = await poRes.json();
        if (Array.isArray(poData)) setPurchaseOrders(poData);
      }
      if (vRes.ok) {
        const vData = await vRes.json();
        if (Array.isArray(vData)) {
          setVendors(vData);
          if (vData.length > 0) {
            setPoForm((prev) => ({ ...prev, vendorId: String(vData[0].id) }));
            setBillForm((prev) => ({ ...prev, vendorId: String(vData[0].id) }));
            setExemptionForm((prev) => ({ ...prev, vendorId: String(vData[0].id) }));
          }
        }
      }
      if (pRes.ok) {
        const pData = await pRes.json();
        if (Array.isArray(pData)) {
          setProjects(pData);
          if (pData.length > 0) {
            setPoForm((prev) => ({ ...prev, projectId: String(pData[0].id) }));
            setBillForm((prev) => ({ ...prev, projectId: String(pData[0].id) }));
            setPrForm((prev) => ({ ...prev, projectId: String(pData[0].id) }));
          }
        }
      }
      if (bRes.ok) {
        const bData = await bRes.json();
        if (Array.isArray(bData)) setSupplierBills(bData);
      }
      if (rRes.ok) {
        const rData = await rRes.json();
        if (Array.isArray(rData)) setRequisitions(rData);
      }
      if (eRes.ok) {
        const eData = await eRes.json();
        if (Array.isArray(eData)) setExemptions(eData);
      }
    } catch (err) {
      console.warn('Purchase data fetch notice:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPurchaseData();
  }, []);

  const handleCreatePo = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const sub = Number(poForm.subtotal || 0);
      const tax = Number(poForm.taxAmount || 0);
      const grandTotal = sub + tax;

      const res = await fetch('/api/purchase-orders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...poForm,
          subtotal: sub.toFixed(2),
          taxAmount: tax.toFixed(2),
          grandTotal: grandTotal.toFixed(2),
        }),
      });
      if (res.ok) {
        setShowAddPoModal(false);
        fetchPurchaseData();
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleCreateBill = async (e: React.FormEvent) => {
    e.preventDefault();
    setBillError(null);
    try {
      const res = await fetch('/api/supplier-bills', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(billForm),
      });
      const data = await res.json();
      if (!res.ok) {
        setBillError(data.error || 'Failed to record supplier bill');
        return;
      }
      setShowAddBillModal(false);
      setBillForm({
        supplierInvoiceNumber: '',
        vendorId: vendors[0]?.id ? String(vendors[0].id) : '',
        poId: '',
        projectId: projects[0]?.id ? String(projects[0].id) : '',
        costCenterType: 'PROJECT_SITE',
        billDate: new Date().toISOString().split('T')[0],
        dueDate: new Date(Date.now() + 30 * 86400000).toISOString().split('T')[0],
        grossBeforeTax: '',
        otherCharges: '0',
        isGstInvoice: true,
        purchaseType: 'STANDARD_PO',
        remarks: '',
      });
      fetchPurchaseData();
    } catch (err: any) {
      setBillError(err.message);
    }
  };

  const handleVerifyBill = async (id: number) => {
    try {
      const res = await fetch(`/api/supplier-bills/${id}/verify`, { method: 'PUT' });
      if (res.ok) fetchPurchaseData();
    } catch (err) {
      console.error(err);
    }
  };

  const handlePayBill = async (id: number) => {
    try {
      const res = await fetch(`/api/supplier-bills/${id}/pay`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (res.ok) fetchPurchaseData();
    } catch (err) {
      console.error(err);
    }
  };

  const handleCreatePr = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const qty = Number(prForm.itemQuantity || 1);
      const rate = Number(prForm.itemEstimatedRate || 0);
      const total = qty * rate;

      const res = await fetch('/api/purchase-requisitions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          projectId: prForm.projectId ? Number(prForm.projectId) : null,
          requiredDate: prForm.requiredDate,
          department: prForm.department,
          urgencyLevel: prForm.urgencyLevel,
          isDirectSiteDelivery: prForm.isDirectSiteDelivery,
          reason: prForm.reason,
          estimatedTotal: total,
          items: [
            {
              description: prForm.itemDescription,
              quantity: qty,
              unit: prForm.itemUnit,
              estimatedRate: rate,
              estimatedAmount: total,
            },
          ],
        }),
      });
      if (res.ok) {
        setShowAddPrModal(false);
        fetchPurchaseData();
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleApprovePr = async (id: number) => {
    try {
      const res = await fetch(`/api/purchase-requisitions/${id}/approve`, { method: 'PUT' });
      if (res.ok) fetchPurchaseData();
    } catch (err) {
      console.error(err);
    }
  };

  const handleCreateExemption = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const res = await fetch('/api/supplier-exemptions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(exemptionForm),
      });
      if (res.ok) {
        setShowAddExemptionModal(false);
        fetchPurchaseData();
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleVerifyExemption = async (id: number, status: 'VERIFIED' | 'REJECTED') => {
    try {
      const res = await fetch(`/api/supplier-exemptions/${id}/verify`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ verificationStatus: status }),
      });
      if (res.ok) fetchPurchaseData();
    } catch (err) {
      console.error(err);
    }
  };

  const formatPKR = (val: string | number) => {
    return 'Rs. ' + Math.round(Number(val || 0)).toLocaleString('en-PK');
  };

  // Live bill preview
  const selectedVendor = vendors.find((v) => String(v.id) === String(billForm.vendorId));
  const hasExemption = Boolean(
    selectedVendor?.isTaxExempt ||
      exemptions.some((e) => e.vendorId === selectedVendor?.id && e.verificationStatus === 'VERIFIED')
  );
  const liveTaxPreview = calculateSupplierBillTaxes({
    grossBeforeTax: billForm.grossBeforeTax || 0,
    supplierCategory: selectedVendor?.supplierCategory || 'REGISTERED',
    hasGstInvoice: billForm.isGstInvoice,
    hasVerifiedExemptionDoc: hasExemption,
    exemptionValid: hasExemption,
    configuredGstRate: 18.0,
    otherCharges: billForm.otherCharges || 0,
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">
              Purchase Management &amp; Tax Engine
            </h1>
            <span className="px-2 py-0.5 rounded text-[11px] font-bold bg-amber-100 text-amber-900 border border-amber-200">
              FBR &amp; PRA Compliant
            </span>
          </div>
          <p className="text-xs sm:text-sm text-slate-500 mt-0.5">
            Purchase Requisitions, Purchase Orders, Supplier Bills with Duplicate Prevention, and Tax Withholding (18% GST / 5.5% / 11% / 0% Exempt).
          </p>
        </div>

        {/* Action Button based on tab */}
        <div className="flex items-center gap-2">
          {activeTab === 'POS' && (
            <button
              onClick={() => setShowAddPoModal(true)}
              className="flex items-center gap-1.5 px-3.5 py-2 bg-[#0f2942] hover:bg-[#183d5f] text-white rounded-lg text-xs font-semibold shadow-2xs transition-colors"
            >
              <Plus className="w-4 h-4 text-amber-400" />
              <span>Issue Purchase Order</span>
            </button>
          )}
          {activeTab === 'BILLS' && (
            <button
              onClick={() => setShowAddBillModal(true)}
              className="flex items-center gap-1.5 px-3.5 py-2 bg-emerald-700 hover:bg-emerald-800 text-white rounded-lg text-xs font-semibold shadow-2xs transition-colors"
            >
              <Plus className="w-4 h-4 text-emerald-200" />
              <span>Record Supplier Bill</span>
            </button>
          )}
          {activeTab === 'REQUISITIONS' && (
            <button
              onClick={() => setShowAddPrModal(true)}
              className="flex items-center gap-1.5 px-3.5 py-2 bg-blue-700 hover:bg-blue-800 text-white rounded-lg text-xs font-semibold shadow-2xs transition-colors"
            >
              <Plus className="w-4 h-4 text-blue-200" />
              <span>New Purchase Requisition</span>
            </button>
          )}
          {activeTab === 'SUPPLIERS' && (
            <button
              onClick={() => setShowAddExemptionModal(true)}
              className="flex items-center gap-1.5 px-3.5 py-2 bg-purple-700 hover:bg-purple-800 text-white rounded-lg text-xs font-semibold shadow-2xs transition-colors"
            >
              <ShieldCheck className="w-4 h-4 text-purple-200" />
              <span>Add Exemption Certificate</span>
            </button>
          )}
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="flex items-center gap-2 border-b border-slate-200 overflow-x-auto text-xs font-bold">
        <button
          onClick={() => setActiveTab('POS')}
          className={`pb-3 px-3 border-b-2 transition-colors flex items-center gap-2 ${
            activeTab === 'POS'
              ? 'border-blue-600 text-blue-900'
              : 'border-transparent text-slate-500 hover:text-slate-800'
          }`}
        >
          <ShoppingCart className="w-4 h-4" />
          <span>Purchase Orders ({purchaseOrders.length})</span>
        </button>

        <button
          onClick={() => setActiveTab('BILLS')}
          className={`pb-3 px-3 border-b-2 transition-colors flex items-center gap-2 ${
            activeTab === 'BILLS'
              ? 'border-emerald-600 text-emerald-900'
              : 'border-transparent text-slate-500 hover:text-slate-800'
          }`}
        >
          <Receipt className="w-4 h-4" />
          <span>Supplier Bills &amp; Tax ({supplierBills.length})</span>
        </button>

        <button
          onClick={() => setActiveTab('REQUISITIONS')}
          className={`pb-3 px-3 border-b-2 transition-colors flex items-center gap-2 ${
            activeTab === 'REQUISITIONS'
              ? 'border-indigo-600 text-indigo-900'
              : 'border-transparent text-slate-500 hover:text-slate-800'
          }`}
        >
          <FileText className="w-4 h-4" />
          <span>Purchase Requisitions ({requisitions.length})</span>
        </button>

        <button
          onClick={() => setActiveTab('SUPPLIERS')}
          className={`pb-3 px-3 border-b-2 transition-colors flex items-center gap-2 ${
            activeTab === 'SUPPLIERS'
              ? 'border-purple-600 text-purple-900'
              : 'border-transparent text-slate-500 hover:text-slate-800'
          }`}
        >
          <ShieldCheck className="w-4 h-4" />
          <span>Supplier Tax Classifications &amp; Exemptions ({vendors.length})</span>
        </button>
      </div>

      {/* TAB 1: PURCHASE ORDERS */}
      {activeTab === 'POS' && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-2xs overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="bg-slate-50 text-slate-500 border-b border-slate-200 uppercase tracking-wider text-[10px]">
                <tr>
                  <th className="py-3 px-4">PO Number</th>
                  <th className="py-3 px-4">Supplier / Vendor</th>
                  <th className="py-3 px-4">Target Project</th>
                  <th className="py-3 px-4">PO Date</th>
                  <th className="py-3 px-4">Grand Total (PKR)</th>
                  <th className="py-3 px-4">Status</th>
                  <th className="py-3 px-4 text-right">Vendor Dispatch</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(Array.isArray(purchaseOrders) ? purchaseOrders : []).map((po) => (
                  <tr key={po.id} className="hover:bg-slate-50/80 transition-colors">
                    <td className="py-3 px-4 font-bold text-blue-900">{po.poNumber}</td>
                    <td className="py-3 px-4 font-semibold text-slate-800">{po.vendorName}</td>
                    <td className="py-3 px-4 text-slate-700">{po.projectName}</td>
                    <td className="py-3 px-4 text-slate-500">{po.poDate}</td>
                    <td className="py-3 px-4 font-bold text-slate-900">{formatPKR(po.grandTotal)}</td>
                    <td className="py-3 px-4">
                      <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-100 text-blue-800">
                        {po.status}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <button
                        type="button"
                        onClick={() => {
                          setShareData({
                            title: `Purchase Order - ${po.poNumber}`,
                            docType: 'PURCHASE_ORDER',
                            referenceNumber: po.poNumber,
                            projectName: po.projectName,
                            recipientName: po.vendorName,
                            amount: formatPKR(po.grandTotal),
                            date: po.poDate,
                            detailsSummary: `Supplier PO issued to ${po.vendorName}. Target Delivery: ${
                              po.deliveryDate || 'Standard'
                            }. Total Value: ${formatPKR(po.grandTotal)}. Terms: ${po.terms || '30 days net'}`,
                          });
                          setWhatsAppModalOpen(true);
                        }}
                        className="text-xs font-bold text-[#075e54] hover:text-white flex items-center gap-1.5 bg-[#25D366]/15 hover:bg-[#25D366] px-2.5 py-1.5 rounded-lg transition-colors ml-auto"
                        title="Send PO to vendor on WhatsApp"
                      >
                        <MessageSquare className="w-3.5 h-3.5 text-[#25D366] group-hover:text-white" />
                        <span>WhatsApp PO</span>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* TAB 2: SUPPLIER BILLS & TAX */}
      {activeTab === 'BILLS' && (
        <div className="space-y-4">
          {/* Quick Notice about Tax Engine Rules */}
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs text-slate-700 flex items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-emerald-600 flex-shrink-0" />
              <span>
                <strong>Tax Engine Rules:</strong> Registered suppliers (18% GST, 5.5% WHT deducted from payment) | Local purchase without GST invoice (11% WHT recorded separately without reducing purchase cost) | Verified Exempt (0% WHT with valid certificate).
              </span>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 shadow-2xs overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="bg-slate-50 text-slate-500 border-b border-slate-200 uppercase tracking-wider text-[10px]">
                  <tr>
                    <th className="py-3 px-4">Bill # / Vendor Inv #</th>
                    <th className="py-3 px-4">Supplier &amp; Category</th>
                    <th className="py-3 px-4">Bill Date</th>
                    <th className="py-3 px-4">Gross Before Tax</th>
                    <th className="py-3 px-4">18% GST</th>
                    <th className="py-3 px-4">Gross Total</th>
                    <th className="py-3 px-4">WHT Rate / Amount</th>
                    <th className="py-3 px-4">Net Payable</th>
                    <th className="py-3 px-4">Status</th>
                    <th className="py-3 px-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {supplierBills.map((bill) => (
                    <tr key={bill.id} className="hover:bg-slate-50/80 transition-colors">
                      <td className="py-3 px-4">
                        <div className="font-bold text-slate-900">{bill.billNumber}</div>
                        <div className="text-[11px] text-slate-500">Inv: {bill.supplierInvoiceNumber}</div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="font-semibold text-slate-800">{bill.vendorName || `Vendor #${bill.vendorId}`}</div>
                        <span className="inline-block px-1.5 py-0.5 rounded text-[9px] font-semibold bg-slate-100 text-slate-700">
                          {bill.vendorCategory || 'SUPPLIER'}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-slate-600">{bill.billDate}</td>
                      <td className="py-3 px-4 text-slate-700">{formatPKR(bill.grossBeforeTax)}</td>
                      <td className="py-3 px-4 text-slate-700">
                        {Number(bill.gstAmount) > 0 ? (
                          <span className="text-emerald-700 font-semibold">{formatPKR(bill.gstAmount)} ({bill.gstRate}%)</span>
                        ) : (
                          <span className="text-slate-400">Rs. 0.00</span>
                        )}
                      </td>
                      <td className="py-3 px-4 font-bold text-slate-900">{formatPKR(bill.grossTotal)}</td>
                      <td className="py-3 px-4">
                        {Number(bill.withholdingAmount) > 0 ? (
                          <div>
                            <span className="font-bold text-amber-900">{formatPKR(bill.withholdingAmount)}</span>
                            <span className="text-[10px] text-slate-500 block">({bill.withholdingRate}%)</span>
                          </div>
                        ) : (
                          <span className="text-emerald-600 font-bold text-[11px]">0% (Exempt)</span>
                        )}
                      </td>
                      <td className="py-3 px-4 font-bold text-blue-900">{formatPKR(bill.netPayable)}</td>
                      <td className="py-3 px-4">
                        <div className="space-y-1">
                          <span
                            className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold ${
                              bill.verificationStatus === 'VERIFIED'
                                ? 'bg-emerald-100 text-emerald-800'
                                : 'bg-amber-100 text-amber-800'
                            }`}
                          >
                            {bill.verificationStatus}
                          </span>
                          <span
                            className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold block ${
                              bill.paymentStatus === 'PAID'
                                ? 'bg-blue-100 text-blue-800'
                                : 'bg-slate-100 text-slate-700'
                            }`}
                          >
                            {bill.paymentStatus}
                          </span>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-right space-y-1">
                        {bill.verificationStatus !== 'VERIFIED' && (
                          <button
                            onClick={() => handleVerifyBill(bill.id)}
                            className="px-2 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-[10px] font-bold shadow-2xs"
                          >
                            Verify Bill
                          </button>
                        )}
                        {bill.verificationStatus === 'VERIFIED' && bill.paymentStatus !== 'PAID' && (
                          <button
                            onClick={() => handlePayBill(bill.id)}
                            className="px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-[10px] font-bold shadow-2xs ml-1"
                          >
                            Record Payout
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* TAB 3: PURCHASE REQUISITIONS */}
      {activeTab === 'REQUISITIONS' && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-2xs overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="bg-slate-50 text-slate-500 border-b border-slate-200 uppercase tracking-wider text-[10px]">
                <tr>
                  <th className="py-3 px-4">PR Number</th>
                  <th className="py-3 px-4">Department / Requested By</th>
                  <th className="py-3 px-4">Target Project</th>
                  <th className="py-3 px-4">Required Date</th>
                  <th className="py-3 px-4">Urgency</th>
                  <th className="py-3 px-4">Estimated Total</th>
                  <th className="py-3 px-4">Status</th>
                  <th className="py-3 px-4 text-right">Approval</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {requisitions.map((pr) => (
                  <tr key={pr.id} className="hover:bg-slate-50/80 transition-colors">
                    <td className="py-3 px-4 font-bold text-blue-900">{pr.prNumber}</td>
                    <td className="py-3 px-4">
                      <div className="font-semibold text-slate-800">{pr.requestedBy}</div>
                      <div className="text-[11px] text-slate-500">{pr.department}</div>
                    </td>
                    <td className="py-3 px-4 text-slate-700">{pr.projectName || `Project #${pr.projectId || 'N/A'}`}</td>
                    <td className="py-3 px-4 text-slate-600">{pr.requiredDate}</td>
                    <td className="py-3 px-4">
                      <span
                        className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold ${
                          pr.urgencyLevel === 'EMERGENCY' || pr.urgencyLevel === 'HIGH'
                            ? 'bg-rose-100 text-rose-800'
                            : 'bg-slate-100 text-slate-700'
                        }`}
                      >
                        {pr.urgencyLevel}
                      </span>
                    </td>
                    <td className="py-3 px-4 font-bold text-slate-900">{formatPKR(pr.estimatedTotal)}</td>
                    <td className="py-3 px-4">
                      <span
                        className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold ${
                          pr.status === 'APPROVED' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'
                        }`}
                      >
                        {pr.status}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right">
                      {pr.status !== 'APPROVED' ? (
                        <button
                          onClick={() => handleApprovePr(pr.id)}
                          className="px-2.5 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-[11px] font-bold shadow-2xs"
                        >
                          Approve PR
                        </button>
                      ) : (
                        <span className="text-[11px] text-emerald-700 font-semibold">Approved by {pr.approvedBy || 'Director'}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* TAB 4: SUPPLIERS & EXEMPTIONS */}
      {activeTab === 'SUPPLIERS' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-slate-200 shadow-2xs overflow-hidden">
            <div className="p-4 border-b border-slate-100 font-bold text-slate-900 text-sm flex items-center justify-between">
              <span>Supplier Master Tax Classifications</span>
              <span className="text-xs text-slate-500 font-normal">Active Suppliers: {vendors.length}</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="bg-slate-50 text-slate-500 border-b border-slate-200 uppercase tracking-wider text-[10px]">
                  <tr>
                    <th className="py-3 px-4">Supplier Code &amp; Name</th>
                    <th className="py-3 px-4">Category</th>
                    <th className="py-3 px-4">NTN / STRN</th>
                    <th className="py-3 px-4">GST Invoice Capability</th>
                    <th className="py-3 px-4">Income Tax Classification</th>
                    <th className="py-3 px-4">Exemption Status</th>
                    <th className="py-3 px-4">Payment Terms</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {vendors.map((v) => (
                    <tr key={v.id} className="hover:bg-slate-50/80 transition-colors">
                      <td className="py-3 px-4">
                        <div className="font-bold text-blue-900">{v.name}</div>
                        <div className="text-[11px] text-slate-500">{v.code} | {v.phone}</div>
                      </td>
                      <td className="py-3 px-4">
                        <span className="inline-block px-2 py-0.5 rounded text-[10px] font-bold bg-slate-100 text-slate-800">
                          {v.supplierCategory || v.category}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-slate-700">
                        <div>NTN: {v.ntn || 'Not Registered'}</div>
                        <div className="text-[11px] text-slate-500">STRN: {v.strn || 'None'}</div>
                      </td>
                      <td className="py-3 px-4">
                        {v.hasGstInvoiceCapability ? (
                          <span className="text-emerald-700 font-bold flex items-center gap-1">
                            <CheckCircle2 className="w-3.5 h-3.5" /> GST Invoice Enabled (18%)
                          </span>
                        ) : (
                          <span className="text-amber-800 font-bold text-[11px]">
                            No GST Invoice (11% WHT)
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-slate-700 font-medium">
                        {v.incomeTaxClassification || 'STANDARD_SUPPLIER'}
                      </td>
                      <td className="py-3 px-4">
                        {v.isTaxExempt ? (
                          <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-bold bg-purple-100 text-purple-800">
                            Verified Exempt (0% WHT)
                          </span>
                        ) : (
                          <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-bold bg-slate-100 text-slate-700">
                            Standard Taxable
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-slate-600">{v.paymentTerms}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Exemption Certificates Table */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-2xs overflow-hidden">
            <div className="p-4 border-b border-slate-100 font-bold text-slate-900 text-sm flex items-center justify-between">
              <span>Verified FBR Section 153 Exemption Certificates Registry</span>
              <span className="text-xs text-slate-500 font-normal">Active Certificates: {exemptions.length}</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="bg-slate-50 text-slate-500 border-b border-slate-200 uppercase tracking-wider text-[10px]">
                  <tr>
                    <th className="py-3 px-4">Certificate Ref #</th>
                    <th className="py-3 px-4">Supplier</th>
                    <th className="py-3 px-4">Document Type</th>
                    <th className="py-3 px-4">Issuing Authority</th>
                    <th className="py-3 px-4">Validity Range</th>
                    <th className="py-3 px-4">Verification Status</th>
                    <th className="py-3 px-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {exemptions.map((ex) => (
                    <tr key={ex.id} className="hover:bg-slate-50/80 transition-colors">
                      <td className="py-3 px-4 font-bold text-purple-900">{ex.referenceNumber}</td>
                      <td className="py-3 px-4 font-semibold text-slate-800">
                        {ex.vendorName || `Vendor #${ex.vendorId}`}
                      </td>
                      <td className="py-3 px-4 text-slate-700">{ex.documentType}</td>
                      <td className="py-3 px-4 text-slate-600">{ex.issuingAuthority}</td>
                      <td className="py-3 px-4 text-slate-600">
                        {ex.validFrom} to {ex.validTo}
                      </td>
                      <td className="py-3 px-4">
                        <span
                          className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold ${
                            ex.verificationStatus === 'VERIFIED'
                              ? 'bg-purple-100 text-purple-800'
                              : 'bg-amber-100 text-amber-800'
                          }`}
                        >
                          {ex.verificationStatus}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right space-x-1">
                        {ex.verificationStatus !== 'VERIFIED' && (
                          <button
                            onClick={() => handleVerifyExemption(ex.id, 'VERIFIED')}
                            className="px-2 py-1 bg-purple-600 hover:bg-purple-700 text-white rounded text-[10px] font-bold"
                          >
                            Approve
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* MODAL 1: ADD PO */}
      {showAddPoModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
          <div className="bg-white rounded-xl shadow-2xl border border-slate-200 max-w-lg w-full p-5 space-y-4 animate-in fade-in zoom-in-95 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <h3 className="font-bold text-slate-900 text-base">Issue Purchase Order</h3>
              <button
                onClick={() => setShowAddPoModal(false)}
                className="text-slate-400 hover:text-slate-600 text-sm font-semibold"
              >
                ✕
              </button>
            </div>
            <form onSubmit={handleCreatePo} className="space-y-3 text-xs">
              <div>
                <label className="block font-semibold text-slate-700 mb-1">Target Project</label>
                <select
                  value={poForm.projectId}
                  onChange={(e) => setPoForm({ ...poForm, projectId: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                  required
                >
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.projectNumber} - {p.projectName}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block font-semibold text-slate-700 mb-1">Vendor / Material Supplier</label>
                <select
                  value={poForm.vendorId}
                  onChange={(e) => setPoForm({ ...poForm, vendorId: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                  required
                >
                  {vendors.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.name} ({v.category})
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Delivery Target Date</label>
                  <input
                    type="date"
                    value={poForm.deliveryDate}
                    onChange={(e) => setPoForm({ ...poForm, deliveryDate: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Subtotal Amount (PKR)</label>
                  <input
                    type="number"
                    step="0.01"
                    placeholder="e.g. 500000"
                    value={poForm.subtotal}
                    onChange={(e) => setPoForm({ ...poForm, subtotal: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block font-semibold text-slate-700 mb-1">Terms &amp; Conditions</label>
                <textarea
                  rows={2}
                  value={poForm.terms}
                  onChange={(e) => setPoForm({ ...poForm, terms: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setShowAddPoModal(false)}
                  className="px-3 py-1.5 text-slate-600 hover:bg-slate-100 rounded-lg font-semibold"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-1.5 bg-[#0f2942] hover:bg-[#183d5f] text-white rounded-lg font-semibold shadow-2xs"
                >
                  Confirm &amp; Issue PO
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL 2: RECORD SUPPLIER BILL (WITH TAX CALCULATION & DUPLICATE PROTECTION) */}
      {showAddBillModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
          <div className="bg-white rounded-xl shadow-2xl border border-slate-200 max-w-xl w-full p-5 space-y-4 animate-in fade-in zoom-in-95 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div>
                <h3 className="font-bold text-slate-900 text-base">Record Supplier Bill</h3>
                <p className="text-xs text-slate-500">Automated FBR 18% GST and Section 153 Withholding Engine</p>
              </div>
              <button
                onClick={() => setShowAddBillModal(false)}
                className="text-slate-400 hover:text-slate-600 text-sm font-semibold"
              >
                ✕
              </button>
            </div>

            {billError && (
              <div className="p-3 bg-rose-50 border border-rose-200 rounded-lg text-xs text-rose-800 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-rose-600 flex-shrink-0" />
                <span>{billError}</span>
              </div>
            )}

            <form onSubmit={handleCreateBill} className="space-y-3 text-xs">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Vendor / Supplier</label>
                  <select
                    value={billForm.vendorId}
                    onChange={(e) => setBillForm({ ...billForm, vendorId: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                    required
                  >
                    {vendors.map((v) => (
                      <option key={v.id} value={v.id}>
                        {v.name} ({v.supplierCategory || v.category})
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">
                    Supplier Invoice / Bill # (Unique per vendor)
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. STC-INV-9901"
                    value={billForm.supplierInvoiceNumber}
                    onChange={(e) => setBillForm({ ...billForm, supplierInvoiceNumber: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg font-semibold"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Target Project</label>
                  <select
                    value={billForm.projectId}
                    onChange={(e) => setBillForm({ ...billForm, projectId: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                  >
                    <option value="">General Company Purchase</option>
                    {projects.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.projectNumber} - {p.projectName}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Cost Center Type</label>
                  <select
                    value={billForm.costCenterType}
                    onChange={(e) => setBillForm({ ...billForm, costCenterType: e.target.value as any })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                  >
                    <option value="PROJECT_SITE">Project / Site Direct</option>
                    <option value="GENERAL_COMPANY">General Office / Company Overhead</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Gross Before Tax (PKR)</label>
                  <input
                    type="number"
                    step="0.01"
                    placeholder="e.g. 100000"
                    value={billForm.grossBeforeTax}
                    onChange={(e) => setBillForm({ ...billForm, grossBeforeTax: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg font-bold"
                    required
                  />
                </div>
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Other Delivery / Freight Charges</label>
                  <input
                    type="number"
                    step="0.01"
                    value={billForm.otherCharges}
                    onChange={(e) => setBillForm({ ...billForm, otherCharges: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                  />
                </div>
              </div>

              <div className="flex items-center gap-2 p-2.5 bg-slate-50 border border-slate-200 rounded-lg">
                <input
                  type="checkbox"
                  id="hasGstInvoice"
                  checked={billForm.isGstInvoice}
                  onChange={(e) => setBillForm({ ...billForm, isGstInvoice: e.target.checked })}
                  className="rounded text-blue-600 w-4 h-4"
                />
                <label htmlFor="hasGstInvoice" className="font-semibold text-slate-800 cursor-pointer text-xs">
                  Valid GST Sales Tax Invoice Attached (Enables 18% Input GST Claim)
                </label>
              </div>

              {/* LIVE TAX BREAKDOWN DISPLAY */}
              <div className="bg-blue-50/60 border border-blue-200 rounded-lg p-3 space-y-1 text-xs">
                <div className="font-bold text-blue-950 flex items-center justify-between">
                  <span>Engine Calculated Breakdown:</span>
                  <span className="text-[11px] font-semibold text-blue-800">
                    Classification: {liveTaxPreview.taxClassification}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-slate-700 pt-1">
                  <div>Gross Before Tax: {formatPKR(liveTaxPreview.grossBeforeTax)}</div>
                  <div>
                    GST ({liveTaxPreview.gstRate}%): {formatPKR(liveTaxPreview.gstAmount)}
                  </div>
                  <div>Gross Total: {formatPKR(liveTaxPreview.grossTotal)}</div>
                  <div>
                    Withholding ({liveTaxPreview.withholdingRate}%): {formatPKR(liveTaxPreview.withholdingAmount)}
                  </div>
                </div>
                <div className="pt-2 border-t border-blue-200 flex items-center justify-between font-bold text-blue-900 text-sm">
                  <span>Net Payable to Vendor:</span>
                  <span>{formatPKR(liveTaxPreview.netPayable)}</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Bill Date</label>
                  <input
                    type="date"
                    value={billForm.billDate}
                    onChange={(e) => setBillForm({ ...billForm, billDate: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Due Date</label>
                  <input
                    type="date"
                    value={billForm.dueDate}
                    onChange={(e) => setBillForm({ ...billForm, dueDate: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                  />
                </div>
              </div>

              <div>
                <label className="block font-semibold text-slate-700 mb-1">Remarks / Audit Note</label>
                <input
                  type="text"
                  placeholder="e.g. Clifton branch flooring delivery inspected by Bilal Hashmi"
                  value={billForm.remarks}
                  onChange={(e) => setBillForm({ ...billForm, remarks: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setShowAddBillModal(false)}
                  className="px-3 py-1.5 text-slate-600 hover:bg-slate-100 rounded-lg font-semibold"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-1.5 bg-emerald-700 hover:bg-emerald-800 text-white rounded-lg font-semibold shadow-2xs"
                >
                  Save &amp; Record Bill
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL 3: PURCHASE REQUISITION */}
      {showAddPrModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
          <div className="bg-white rounded-xl shadow-2xl border border-slate-200 max-w-lg w-full p-5 space-y-4 animate-in fade-in zoom-in-95 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <h3 className="font-bold text-slate-900 text-base">Create Purchase Requisition</h3>
              <button
                onClick={() => setShowAddPrModal(false)}
                className="text-slate-400 hover:text-slate-600 text-sm font-semibold"
              >
                ✕
              </button>
            </div>
            <form onSubmit={handleCreatePr} className="space-y-3 text-xs">
              <div>
                <label className="block font-semibold text-slate-700 mb-1">Target Project</label>
                <select
                  value={prForm.projectId}
                  onChange={(e) => setPrForm({ ...prForm, projectId: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                  required
                >
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.projectNumber} - {p.projectName}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Urgency Level</label>
                  <select
                    value={prForm.urgencyLevel}
                    onChange={(e) => setPrForm({ ...prForm, urgencyLevel: e.target.value as any })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                  >
                    <option value="LOW">Low</option>
                    <option value="MEDIUM">Medium</option>
                    <option value="HIGH">High</option>
                    <option value="EMERGENCY">Emergency (Site Halt)</option>
                  </select>
                </div>
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Required By Date</label>
                  <input
                    type="date"
                    value={prForm.requiredDate}
                    onChange={(e) => setPrForm({ ...prForm, requiredDate: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block font-semibold text-slate-700 mb-1">Item Description</label>
                <input
                  type="text"
                  placeholder="e.g. Master Porcelain Tiles 600x600 Matt Finish"
                  value={prForm.itemDescription}
                  onChange={(e) => setPrForm({ ...prForm, itemDescription: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg font-semibold"
                  required
                />
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Quantity</label>
                  <input
                    type="number"
                    value={prForm.itemQuantity}
                    onChange={(e) => setPrForm({ ...prForm, itemQuantity: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                    required
                  />
                </div>
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Unit</label>
                  <input
                    type="text"
                    value={prForm.itemUnit}
                    onChange={(e) => setPrForm({ ...prForm, itemUnit: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                    required
                  />
                </div>
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Estimated Unit Rate (PKR)</label>
                  <input
                    type="number"
                    value={prForm.itemEstimatedRate}
                    onChange={(e) => setPrForm({ ...prForm, itemEstimatedRate: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block font-semibold text-slate-700 mb-1">Reason / Site Justification</label>
                <textarea
                  rows={2}
                  value={prForm.reason}
                  onChange={(e) => setPrForm({ ...prForm, reason: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                  required
                />
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setShowAddPrModal(false)}
                  className="px-3 py-1.5 text-slate-600 hover:bg-slate-100 rounded-lg font-semibold"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-1.5 bg-blue-700 hover:bg-blue-800 text-white rounded-lg font-semibold shadow-2xs"
                >
                  Submit Requisition
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL 4: ADD EXEMPTION DOCUMENT */}
      {showAddExemptionModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
          <div className="bg-white rounded-xl shadow-2xl border border-slate-200 max-w-lg w-full p-5 space-y-4 animate-in fade-in zoom-in-95 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <h3 className="font-bold text-slate-900 text-base">Add Supplier Exemption Certificate</h3>
              <button
                onClick={() => setShowAddExemptionModal(false)}
                className="text-slate-400 hover:text-slate-600 text-sm font-semibold"
              >
                ✕
              </button>
            </div>
            <form onSubmit={handleCreateExemption} className="space-y-3 text-xs">
              <div>
                <label className="block font-semibold text-slate-700 mb-1">Supplier</label>
                <select
                  value={exemptionForm.vendorId}
                  onChange={(e) => setExemptionForm({ ...exemptionForm, vendorId: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                  required
                >
                  {vendors.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.name} ({v.supplierCategory || v.category})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block font-semibold text-slate-700 mb-1">FBR Certificate Reference #</label>
                <input
                  type="text"
                  placeholder="e.g. FBR-EX-2026-9901"
                  value={exemptionForm.referenceNumber}
                  onChange={(e) => setExemptionForm({ ...exemptionForm, referenceNumber: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg font-bold"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Valid From</label>
                  <input
                    type="date"
                    value={exemptionForm.validFrom}
                    onChange={(e) => setExemptionForm({ ...exemptionForm, validFrom: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                    required
                  />
                </div>
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Valid To</label>
                  <input
                    type="date"
                    value={exemptionForm.validTo}
                    onChange={(e) => setExemptionForm({ ...exemptionForm, validTo: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block font-semibold text-slate-700 mb-1">Issuing Authority</label>
                <input
                  type="text"
                  value={exemptionForm.issuingAuthority}
                  onChange={(e) => setExemptionForm({ ...exemptionForm, issuingAuthority: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setShowAddExemptionModal(false)}
                  className="px-3 py-1.5 text-slate-600 hover:bg-slate-100 rounded-lg font-semibold"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-1.5 bg-purple-700 hover:bg-purple-800 text-white rounded-lg font-semibold shadow-2xs"
                >
                  Upload &amp; Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* WhatsApp Modal */}
      {shareData && (
        <WhatsAppShareModal
          isOpen={whatsAppModalOpen}
          onClose={() => setWhatsAppModalOpen(false)}
          data={shareData}
        />
      )}
    </div>
  );
};
